import { useState, useEffect } from 'react';
import { ChartContainer } from '@/components/atoms/Chart';
import { AreaChart, Area, XAxis } from 'recharts';
import { ChartTooltip, ChartTooltipContent } from '@/components/atoms/Chart';
import { EcommerceNewsItem } from '../mock/getMockNews';
import { logEvent } from '@/etc/firebase';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';

interface LabelProps {
  index?: number;
  x?: number;
  y?: number;
  value?: number;
}

export const NewsCharts = ({
  keywordCount,
  active,
  isDashboard = false,
}: {
  keywordCount: EcommerceNewsItem['keywordCount'];
  active: boolean;
  isDashboard?: boolean;
}) => {
  const keywords = Object.keys(keywordCount);
  const [selected, setSelected] = useState(keywords[0]);
  const { group } = useParams(); // apage 또는 bpage

  // keywords가 변경될 때마다 selected를 업데이트
  useEffect(() => {
    if (keywords.length > 0) {
      setSelected(keywords[0]);
    }
  }, [keywords.join(',')]);

  const handleClick = (keyword: string) => () => {
    setSelected(keyword);
    logEvent('select_news_keyword', {
      keyword,
      targetPage: isDashboard ? 'pc_page' : group,
    });
  };

  const formattedData = useMemo(() => {
    return (
      keywordCount[selected]?.map((item) => {
        const [, month, day] = item.date.split('-');
        return {
          date: `${month}/${day}`,
          언급량: item.count,
        };
      }) ?? []
    );
  }, [selected, keywordCount]);

  // 최대값 인덱스 계산
  const maxIndex = useMemo(() => {
    let max = -Infinity;
    let idx = -1;
    formattedData.forEach((d, i) => {
      if (d.언급량 > max) {
        max = d.언급량;
        idx = i;
      }
    });
    return idx;
  }, [formattedData]);

  return (
    <div className={`flex flex-col ${isDashboard ? 'gap-[5px]' : 'gap-[20px]'}`}>
      <ChartHeader keywords={keywords} selected={selected} onClick={handleClick} />

      <ChartContainer config={{}} className=" h-[100px] min-h-[95px] w-full">
        <AreaChart
          key={`${selected}-${active ? 'active' : 'inactive'}`}
          data={formattedData}
          margin={{ top: 25, right: 16, bottom: 0, left: 16 }}
        >
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            // interval={0}
            interval={'preserveStartEnd'}
            tick={{
              fontSize: isDashboard ? 10 : 12,
              fontWeight: 500,
              fill: '#939393',
              textAnchor: 'middle',
            }}
          />

          <ChartTooltip
            // cursor={false}
            key={active ? `active-${selected}` : `inactive-${selected}`}
            content={<ChartTooltipContent className="rounded-sm bg-white/70" />}
          />

          <defs>
            <linearGradient id="fillMention" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4B93FF" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#4B93FF" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <Area
            key={selected}
            dataKey="언급량"
            // type="natural"
            type="monotone" // 또는 "linear"
            fill="url(#fillMention)"
            fillOpacity={0.4}
            stroke="#4B93FF"
            animationDuration={700}
            dot={({ cx, cy, index }) => (
              <circle
                key={`dot-${index}`}
                cx={cx}
                cy={cy}
                r={4}
                stroke="#4B93FF"
                strokeWidth={2}
                fill="#4B93FF"
              />
            )}
            label={({ index, x, y, value }: LabelProps) =>
              index === maxIndex ? (
                <text
                  x={x}
                  y={y && y - 10}
                  fontSize={isDashboard ? 10 : 12}
                  fontWeight={500}
                  fill="#4B93FF"
                  textAnchor="middle"
                >
                  {value}
                </text>
              ) : null
            }
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
};

const ChartHeader = ({
  keywords,
  selected,
  onClick,
}: {
  keywords: string[];
  selected: string;
  onClick: (keyword: string) => () => void;
}) => {
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="text-gr-800 Body4S14">핵심 키워드 언급량</div>
      <div className="flex flex-wrap gap-[5px]">
        {keywords.map((keyword) => (
          <button
            key={keyword}
            onClick={onClick(keyword)}
            className={`rounded-[4px] border px-[10px] py-[5px] transition-colors duration-300 Caption1M12 ${
              selected === keyword ? 'bg-gr-800 text-white' : 'bg-white text-gr-700 hover:bg-gr-200'
            }`}
          >
            {keyword}
          </button>
        ))}
      </div>
    </div>
  );
};
